import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  AllCommunityModule,
  ModuleRegistry,
  ICellRendererParams,
  IGetRowsParams,
  RowStyle,
} from 'ag-grid-community';
import AddClientModal from '../../components/modals/AddClientModal';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import 'react-toastify/dist/ReactToastify.css';
// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface ClientData {
  _id?: string;
  name: string;
  status: string;
  period?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  active: boolean;
  billingStatus?: string;
  lastPaymentAt?: string;
  [key: string]: any;
}

const BILLING_STATUS_STYLES: Record<string, string> = {
  trial: 'bg-yellow-100 text-yellow-800',
  invoice_draft: 'bg-purple-100 text-purple-800',
  pending: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  invoice_sent: 'bg-blue-100 text-blue-800',
  past_due: 'bg-orange-100 text-orange-800',
  suspended: 'bg-red-100 text-red-800',
  canceled: 'bg-gray-100 text-gray-800',
};

const formatBillingStatus = (status?: string) => {
  if (!status) return 'Trial';

  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const Clients: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isMarkPaidDialogOpen, setIsMarkPaidDialogOpen] = useState(false);
  const [isResetTrialDialogOpen, setIsResetTrialDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<ClientData | null>(null);
  const [clientToArchive, setClientToArchive] = useState<ClientData | null>(null);
  const [clientToMarkPaid, setClientToMarkPaid] = useState<ClientData | null>(null);
  const [clientToResetTrial, setClientToResetTrial] = useState<ClientData | null>(null);
  const [editClient, setEditClient] = useState<ClientData | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [isResettingTrial, setIsResettingTrial] = useState(false);
  const gridApi = useRef<any>(null);
  const navigate = useNavigate();

  // Get user roles to determine permissions
  const canDeleteClients = sessionManager.canDeleteClients();
  const canManageClientBilling = sessionManager.canManageAllClients();

  const handleRowClick = (event: any) => {
    const target = event.event?.target;
    if (target) {
      if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('.ag-cell[col-id="actions"]')) {
        return;
      }
    }

    const clientData = event.data;
    if (clientData && clientData._id) {
      // Clear previous client, season, and game data
      sessionManager.clearClientRelatedData(clientData._id, clientData.name);
      navigate('/seasons');
    }
  };

  const datasource = useMemo(
    () => ({
      getRows: async (getRowsParams: IGetRowsParams) => {
        const page = Math.floor(getRowsParams.startRow / pageSize);
        const limit = getRowsParams.endRow - getRowsParams.startRow;

        // Only show loading on the first request
        if (getRowsParams.startRow === 0) {
          setIsLoading(true);
        }

        try {
          const response = await api.get(API_URLS.GET_CLIENTS, {
            params: {
              page: page + 1,
              limit: limit,
            },
          });
          if (response.data.success) {
            getRowsParams.successCallback(response.data.data, response.data.total);
            // Hide loading after first successful load
            if (getRowsParams.startRow === 0) {
              setIsLoading(false);
            }
          } else {
            showErrorMsg(response.data.message || 'Failed to fetch clients');
            getRowsParams.failCallback();
            setIsLoading(false);
          }
        } catch (error) {
          showErrorMsg('Failed to fetch clients.');
          getRowsParams.failCallback();
          setIsLoading(false);
        }
      },
    }),
    [pageSize],
  );

  // On grid ready, just save the api ref
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    // Ensure proper initial loading state
    setIsLoading(true);
  };

  // Handle pagination changes (including page size changes)
  const onPaginationChanged = (params?: any) => {
    if (!gridApi.current) return;
    const newPageSize = gridApi.current.paginationGetPageSize();
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Edit button logic
  const handleEditClick = (client: ClientData) => {
    // Fetch full client (including lead) before opening modal
    if (client._id) {
      api
        .get(API_URLS.GET_CLIENT_BY_ID, { params: { clientId: client._id } })
        .then((resp) => {
          if (resp.data?.success) {
            setEditClient(resp.data.data);
          } else {
            setEditClient(client); // fallback
          }
          setIsModalOpen(true);
        })
        .catch(() => {
          setEditClient(client); // fallback
          setIsModalOpen(true);
        });
    } else {
      setEditClient(client);
      setIsModalOpen(true);
    }
  };

  // Delete button logic
  const handleDeleteClick = (client: ClientData) => {
    setClientToDelete(client);
    setIsDeleteDialogOpen(true);
  };

  // Archive/Unarchive button logic
  const handleArchiveClick = (client: ClientData) => {
    setClientToArchive(client);
    setIsArchiveDialogOpen(true);
  };

  const handleMarkPaidClick = (client: ClientData) => {
    setClientToMarkPaid(client);
    setIsMarkPaidDialogOpen(true);
  };

  const handleResetTrialClick = (client: ClientData) => {
    setClientToResetTrial(client);
    setIsResetTrialDialogOpen(true);
  };

  // Confirm archive/unarchive
  const handleArchiveConfirm = async () => {
    if (clientToArchive && clientToArchive._id) {
      try {
        const response = await api.put(API_URLS.UPDATE_CLIENT, {
          _id: clientToArchive._id,
          active: !clientToArchive.active,
        });
        if (response.data.success) {
          const action = clientToArchive.active ? 'archived' : 'activated';
          showSuccessMsg(`Client ${action} successfully!`);
          if (gridApi.current) {
            setIsLoading(true);
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to update client status');
        }
      } catch (error) {
        showErrorMsg('Failed to update client status.');
      }
    }
    setIsArchiveDialogOpen(false);
    setClientToArchive(null);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (clientToDelete && clientToDelete._id) {
      try {
        const response = await api.delete(API_URLS.DELETE_CLIENT, {
          params: { _id: clientToDelete._id },
        });
        if (response.data.success) {
          showSuccessMsg('Client deleted successfully!');
          if (gridApi.current) {
            setIsLoading(true);
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to delete client');
        }
      } catch (error) {
        showErrorMsg('Failed to delete client.');
      }
    }
    setIsDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  const handleMarkPaidConfirm = async () => {
    if (!clientToMarkPaid?._id) return;

    try {
      setIsMarkingPaid(true);
      const response = await api.post(`${API_URLS.ADMIN_CLIENTS}/${clientToMarkPaid._id}/mark-paid`);

      if (response.data.success) {
        showSuccessMsg(response.data.message || 'Client payment recorded successfully!');
        if (gridApi.current) {
          setIsLoading(true);
          gridApi.current.purgeInfiniteCache();
        }
      } else {
        showErrorMsg(response.data.message || 'Failed to record payment');
      }
    } catch (error: any) {
      showErrorMsg(error.response?.data?.message || 'Failed to record payment.');
    } finally {
      setIsMarkingPaid(false);
      setIsMarkPaidDialogOpen(false);
      setClientToMarkPaid(null);
    }
  };

  const handleResetTrialConfirm = async () => {
    if (!clientToResetTrial?._id) return;

    try {
      setIsResettingTrial(true);
      const response = await api.post(`${API_URLS.ADMIN_CLIENTS}/${clientToResetTrial._id}/reset-trial`);

      if (response.data.success) {
        showSuccessMsg(response.data.message || 'Client has been reset to trial.');
        if (gridApi.current) {
          setIsLoading(true);
          gridApi.current.purgeInfiniteCache();
        }
      } else {
        showErrorMsg(response.data.message || 'Failed to reset client to trial');
      }
    } catch (error: any) {
      showErrorMsg(error.response?.data?.message || 'Failed to reset client to trial.');
    } finally {
      setIsResettingTrial(false);
      setIsResetTrialDialogOpen(false);
      setClientToResetTrial(null);
    }
  };

  // Only refresh grid after add or update
  const handleClientAddedOrUpdated = () => {
    setIsModalOpen(false);
    setEditClient(null);
    if (gridApi.current) {
      setIsLoading(true);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Only close modal, do not refresh grid
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditClient(null);
  };

  const StatusCellRenderer: React.FC<ICellRendererParams<ClientData>> = ({ value, data }) => {
    const isActive = data?.active;
    const statusText = isActive ? 'Active' : 'Archived';
    const bgColor = isActive ? 'bg-green-100' : 'bg-gray-100';
    const textColor = isActive ? 'text-green-800' : 'text-gray-800';

    return <span className={`px-2 py-1 rounded-full ${bgColor} ${textColor}`}>{statusText}</span>;
  };

  const BillingStatusCellRenderer: React.FC<ICellRendererParams<ClientData>> = ({ data }) => {
    const billingStatus = data?.billingStatus || 'trial';
    const badgeClass = BILLING_STATUS_STYLES[billingStatus] || 'bg-gray-100 text-gray-800';

    return <span className={`px-2 py-1 rounded-full ${badgeClass}`}>{formatBillingStatus(billingStatus)}</span>;
  };

  const ActionsCellRenderer: React.FC<ICellRendererParams<ClientData>> = ({ data }) => {
    if (!data) return null;

    const isActive = data.active;
    const canMarkPaid = ['invoice_draft', 'pending', 'invoice_sent', 'past_due', 'suspended'].includes(
      data.billingStatus || '',
    );
    const archiveTitle = isActive ? 'Archive Client' : 'Activate Client';
    const archiveIcon = isActive ? (
      // Archive box icon for archiving
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M5 8h14l-1 9a2 2 0 01-2 2H8a2 2 0 01-2-2L5 8zM5 8V6a2 2 0 012-2h10a2 2 0 012 2v2m-9 4h4"
        />
      </svg>
    ) : (
      // Refresh/Restore icon for activating
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    );

    return (
      <div className="flex items-center justify-center space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick(data);
          }}
          className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-all"
          title="Edit Client"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
            />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleArchiveClick(data);
          }}
          className={`${
            isActive ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'
          } transform hover:scale-110 transition-all`}
          title={archiveTitle}
        >
          {archiveIcon}
        </button>
        {canManageClientBilling && canMarkPaid && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMarkPaidClick(data);
            }}
            className="text-emerald-600 hover:text-emerald-800 transform hover:scale-110 transition-all"
            title="Mark Client Paid"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        )}
        {canManageClientBilling && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleResetTrialClick(data);
            }}
            className="text-amber-600 hover:text-amber-800 transform hover:scale-110 transition-all"
            title="Reset Client to Trial"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 10h10a4 4 0 110 8H9m-6-8l3-3m-3 3l3 3"
              />
            </svg>
          </button>
        )}
        {canDeleteClients && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(data);
            }}
            className="text-red-600 hover:text-red-800 transform hover:scale-110 transition-all"
            title="Delete Client"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const columnDefs: ColDef<ClientData>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: 'Address',
      field: 'address',
      sortable: false,
      flex: 2,
      minWidth: 220,
      valueGetter: (params) => {
        const d = (params.data || {}) as ClientData;
        const parts = [d.address, d.city, d.state, d.zip].filter(Boolean) as string[];
        return parts.length ? parts.join(', ') : '-';
      },
    },
    {
      headerName: 'Phone',
      field: 'phone',
      sortable: false,
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => p.data?.phone || '-',
    },
    {
      headerName: 'Status',
      field: 'active',
      valueGetter: (params) => (params.data?.active ? 'active' : 'inactive'),
      sortable: true,
      flex: 1,
      cellRenderer: StatusCellRenderer,
      minWidth: 100,
    },
    {
      headerName: 'Billing',
      field: 'billingStatus',
      sortable: true,
      flex: 1.2,
      minWidth: 140,
      valueGetter: (params) => params.data?.billingStatus || 'trial',
      cellRenderer: BillingStatusCellRenderer,
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      width: 210,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 210,
      },
    },
  ];

  // Function to apply alternating row styles
  const getRowStyle = (params: any): RowStyle => {
    const baseStyle = { cursor: 'pointer' };
    if (params.node.rowIndex % 2 === 1) {
      return { ...baseStyle, backgroundColor: '#eafbe3' };
    }
    return baseStyle;
  };

  useEffect(() => {
    return () => {
      // Cleanup effect to close any open modals when the component unmounts or when navigating away
      setIsModalOpen(false);
      setIsDeleteDialogOpen(false);
      setIsArchiveDialogOpen(false);
      setIsMarkPaidDialogOpen(false);
      setIsResetTrialDialogOpen(false);
      setClientToDelete(null);
      setClientToArchive(null);
      setClientToMarkPaid(null);
      setClientToResetTrial(null);
      setEditClient(null);
    };
  }, []);

  return (
    <div className="w-full p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl xss:text-2xl font-bold">Clients</h1>
        <button
          onClick={() => {
            setEditClient(null);
            setIsModalOpen(true);
          }}
          className="bg-[#4eea4d] text-black font-semibold px-4 py-1 rounded hover:bg-[#41ca41] transition-colors shadow-2xl"
        >
          Add Client
        </button>
      </div>
      <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100]}
          cacheBlockSize={pageSize}
          datasource={datasource}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          loading={isLoading}
          overlayLoadingTemplate={
            '<div role="status"><svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg><span class="sr-only">Loading...</span></div>'
          }
          overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No clients found</span>'}
          getRowStyle={getRowStyle}
          onRowClicked={handleRowClick}
          blockLoadDebounceMillis={50}
          maxBlocksInCache={2}
          infiniteInitialRowCount={pageSize}
        />
      </div>
      {isModalOpen && (
        <AddClientModal onClose={handleModalClose} onClientAdded={handleClientAddedOrUpdated} editClient={editClient} />
      )}
      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Client"
          message={`Are you sure you want to delete ${clientToDelete?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
      {isArchiveDialogOpen && clientToArchive && (
        <ConfirmDialog
          isOpen={isArchiveDialogOpen}
          onClose={() => setIsArchiveDialogOpen(false)}
          onConfirm={handleArchiveConfirm}
          title={clientToArchive.active ? 'Archive Client' : 'Activate Client'}
          message={
            clientToArchive.active
              ? `Are you sure you want to archive ${clientToArchive.name}? This will make the client inactive but preserve all data.`
              : `Are you sure you want to activate ${clientToArchive.name}? This will make the client active again.`
          }
          confirmText={clientToArchive.active ? 'Archive' : 'Activate'}
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
      {isMarkPaidDialogOpen && clientToMarkPaid && (
        <ConfirmDialog
          isOpen={isMarkPaidDialogOpen}
          onClose={() => {
            if (!isMarkingPaid) {
              setIsMarkPaidDialogOpen(false);
              setClientToMarkPaid(null);
            }
          }}
          onConfirm={handleMarkPaidConfirm}
          title="Mark Client Paid"
          message={`Record ${clientToMarkPaid.name} as paid by check and reactivate billing access if needed?`}
          confirmText="Mark Paid"
          cancelText="Cancel"
          confirmButtonClass="bg-emerald-600 hover:bg-emerald-700"
          isLoading={isMarkingPaid}
        />
      )}
      {isResetTrialDialogOpen && clientToResetTrial && (
        <ConfirmDialog
          isOpen={isResetTrialDialogOpen}
          onClose={() => {
            if (!isResettingTrial) {
              setIsResetTrialDialogOpen(false);
              setClientToResetTrial(null);
            }
          }}
          onConfirm={handleResetTrialConfirm}
          title="Reset Client to Trial"
          message={`Reset ${clientToResetTrial.name} to Trial? This will reactivate access, reset free games, and clear previous billing lifecycle data.`}
          confirmText="Reset to Trial"
          cancelText="Cancel"
          confirmButtonClass="bg-amber-600 hover:bg-amber-700"
          isLoading={isResettingTrial}
        />
      )}
    </div>
  );
};

export default Clients;
